import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Tooltip, Button, Chip } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import type { VersionSummary } from '../../types/entities';

interface VersionTimelineProps {
  versions: VersionSummary[];
  currentVersionId: string;
  hostname: string;
  onVersionSelect: (version: VersionSummary) => void;
}

export const VersionTimeline: FC<VersionTimelineProps> = ({
  versions,
  currentVersionId,
  hostname,
  onVersionSelect,
}) => {
  const navigate = useNavigate();
  const [selectedVersion, setSelectedVersion] = useState<VersionSummary | null>(null);

  const handleVersionClick = (version: VersionSummary) => {
    setSelectedVersion(version);
    // Don't call onVersionSelect here - let user click "View This Version" button
  };

  // Calculate timeline positions
  const sortedVersions = [...versions].sort(
    (a, b) => new Date(a.uploadTimestamp).getTime() - new Date(b.uploadTimestamp).getTime()
  );

  const timelineStart = new Date(sortedVersions[0].uploadTimestamp).getTime();
  const timelineEnd = new Date(sortedVersions[sortedVersions.length - 1].uploadTimestamp).getTime();
  const timelineRange = timelineEnd - timelineStart || 1;

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      {/* Timeline Header */}
      <Typography variant="h6" gutterBottom>
        Configuration Timeline
      </Typography>

      {/* Timeline Visualization */}
      <Box sx={{ position: 'relative', height: 120, my: 4 }}>
        {/* Timeline Line */}
        <Box
          sx={{
            position: 'absolute',
            top: 50,
            left: 40,
            right: 40,
            height: 2,
            bgcolor: 'divider',
          }}
        />

        {/* Version Dots */}
        {sortedVersions.map((version, index) => {
          const timestamp = new Date(version.uploadTimestamp).getTime();
          const position = ((timestamp - timelineStart) / timelineRange) * 100;
          const isCurrent = version.transactionId === currentVersionId;
          const isSelected = version.transactionId === selectedVersion?.transactionId;

          return (
            <Tooltip
              key={version.transactionId}
              title={
                <Box>
                  <Typography variant="body2">
                    Version {index + 1} of {versions.length}
                  </Typography>
                  <Typography variant="caption">
                    {new Date(version.uploadTimestamp).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {version.interfaceCount} interfaces, {version.vlanCount} VLANs
                  </Typography>
                  {version.changesFromPrevious && (
                    <Box mt={1}>
                      <Typography variant="caption" display="block">
                        Changes from previous:
                      </Typography>
                      <Typography variant="caption" display="block">
                        +{version.changesFromPrevious.interfacesAdded} interfaces
                      </Typography>
                      <Typography variant="caption" display="block">
                        +{version.changesFromPrevious.vlansAdded} VLANs
                      </Typography>
                    </Box>
                  )}
                </Box>
              }
            >
              <Box
                onClick={() => handleVersionClick(version)}
                sx={{
                  position: 'absolute',
                  top: 40,
                  left: `calc(${position}% + 40px)`,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  bgcolor: isCurrent ? 'primary.main' : isSelected ? 'secondary.main' : 'grey.400',
                  border: isSelected ? '3px solid' : '2px solid',
                  borderColor: isSelected ? 'secondary.dark' : 'background.paper',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  transform: isSelected ? 'scale(1.3)' : 'scale(1)',
                  '&:hover': {
                    transform: 'scale(1.3)',
                    zIndex: 10,
                  },
                  zIndex: isCurrent ? 5 : isSelected ? 4 : 1,
                }}
              />
            </Tooltip>
          );
        })}

        {/* Date Labels */}
        <Typography
          variant="caption"
          sx={{ position: 'absolute', top: 80, left: 20 }}
        >
          {new Date(sortedVersions[0].uploadTimestamp).toLocaleDateString()}
        </Typography>
        <Typography
          variant="caption"
          sx={{ position: 'absolute', top: 80, right: 20 }}
        >
          {new Date(sortedVersions[sortedVersions.length - 1].uploadTimestamp).toLocaleDateString()}
          {versions.length > 1 && (
            <Chip
              label="Current"
              size="small"
              color="primary"
              sx={{ ml: 1, verticalAlign: 'middle' }}
            />
          )}
        </Typography>
      </Box>

      {/* Selected Version Details */}
      {selectedVersion && (
        <Box sx={{ mt: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle1" gutterBottom>
            Selected Version
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Uploaded: {new Date(selectedVersion.uploadTimestamp).toLocaleString()}
            {' '}({formatDistanceToNow(new Date(selectedVersion.uploadTimestamp), { addSuffix: true })})
          </Typography>
          <Typography variant="body2" color="text.secondary">
            By: {selectedVersion.uploaderUsername}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Transaction ID: {selectedVersion.transactionId}
          </Typography>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>{selectedVersion.interfaceCount}</strong> interfaces,{' '}
              <strong>{selectedVersion.vlanCount}</strong> VLANs
            </Typography>

            {selectedVersion.changesFromPrevious && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Changes from previous version:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  {selectedVersion.changesFromPrevious.interfacesAdded > 0 && (
                    <Chip
                      label={`+${selectedVersion.changesFromPrevious.interfacesAdded} interfaces`}
                      size="small"
                      color="success"
                    />
                  )}
                  {selectedVersion.changesFromPrevious.interfacesRemoved > 0 && (
                    <Chip
                      label={`-${selectedVersion.changesFromPrevious.interfacesRemoved} interfaces`}
                      size="small"
                      color="error"
                    />
                  )}
                  {selectedVersion.changesFromPrevious.interfacesModified > 0 && (
                    <Chip
                      label={`~${selectedVersion.changesFromPrevious.interfacesModified} interfaces modified`}
                      size="small"
                      color="warning"
                    />
                  )}
                  {selectedVersion.changesFromPrevious.vlansAdded > 0 && (
                    <Chip
                      label={`+${selectedVersion.changesFromPrevious.vlansAdded} VLANs`}
                      size="small"
                      color="success"
                    />
                  )}
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={() => onVersionSelect(selectedVersion)}>
              View This Version
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                navigate(`/switch/${hostname}/compare?versionA=${selectedVersion.transactionId}`);
              }}
            >
              Compare with Another
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};
